import {
  Button, Dialog, DialogContent,
  Grid, MenuItem, TextField, Typography, withWidth,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import StockItemSearch from "../../../Components/StockItems/StockItemSearch";
import NoteSection from "../../../Components/NoteSection";
import ChangesHistory, { ChangesHistoryLayout } from "../../../Components/ChangesHistory";
import DialogAppBar from "../../../Components/DialogAppBar";
import DetailTabs from "../../DeviceDetails/DetailTabs";
import ChatIcon from "@material-ui/icons/Chat";

export const orderStatuses = ["planned", "progress", "completed", "cancelled"];

export function OrderDialog({ open, order, warehouses, saving, error, onClose, onSave, onRefresh, width }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ warehouseId: "", dueDate: "", status: "planned" });
  const [items, setItems] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [editing, setEditing] = useState(!order);
  const [showHistory, setShowHistory] = useState(false);

  const resetForm = () => {
    setForm({
      warehouseId: order?.WarehouseId || "",
      dueDate: order?.dueDate || "",
      status: order?.status || "planned",
    });
    setItems((order?.ManufacturingOrderStockItems || []).map((item) => ({
      stockItemId: item.StockItemId,
      stockItem: item.StockItem,
      orderedQuantity: item.orderedQuantity,
      producedQuantity: item.producedQuantity ?? 0,
    })));
    setValidationError("");
  };

  useEffect(() => {
    if (!open) return;
    resetForm();
    setEditing(!order);
    setShowHistory(false);
    // Reset when another order is opened or refreshed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order]);

  const field = (name) => (event) => setForm({ ...form, [name]: event.target.value });
  const changeQuantity = (id, name, value) => setItems(items.map((item) =>
    item.stockItemId === id ? { ...item, [name]: value } : item,
  ));
  const submit = (event) => {
    event.preventDefault();
    if (!editing || saving) return;
    if (!items.length) {
      setValidationError(t("manufacturing.orderItemsRequired"));
      return;
    }
    setValidationError("");
    onSave({
      ...form,
      items: items.map((item) => {
        const previous = order?.ManufacturingOrderStockItems?.find(
          (row) => row.StockItemId === item.stockItemId,
        );
        const progressChanged = order && Number(item.producedQuantity) !== Number(previous?.producedQuantity ?? 0);
        return {
          stockItemId: item.stockItemId,
          orderedQuantity: item.orderedQuantity,
          ...(progressChanged ? {
            producedQuantity: item.producedQuantity,
            ...(previous ? { expectedProducedQuantity: previous.producedQuantity ?? 0 } : {}),
          } : {}),
        };
      }),
    });
  };

  const formatHistoryChange = (field, value) => {
    if (field === "items" && Array.isArray(value)) {
      const targets = value.map((item) => {
        const stockItem = item.StockItem || (order?.ManufacturingOrderStockItems || [])
          .find((target) => target.StockItemId === item.StockItemId)?.StockItem;
        const name = [stockItem?.sku, stockItem?.name].filter(Boolean).join(" ") ||
          `${t("manufacturing.stockItem")} #${item.StockItemId}`;
        return `${name} - ${t("manufacturing.orderedQuantity")}: ${item.orderedQuantity}` +
          ` - ${t("manufacturing.producedQuantity")}: ${item.producedQuantity ?? 0}`;
      });
      return ["orderQuotaTargets", targets.join("\n") || t("dataRemoved")];
    }
    if (field === "WarehouseId") {
      const warehouse = warehouses.find((row) => String(row.id) === String(value));
      return ["orderWarehouse", warehouse?.label || warehouse?.name || value];
    }
    if (field === "status") {
      // Older history entries retain the previous internal status name.
      const status = value === "in_progress" ? "progress" : value;
      return ["orderStatus", status ? t(`manufacturing.orderStatuses.${status}`, status) : value];
    }
    if (field === "dueDate") return ["orderDeadline", value];
    if (field === "manufacturingCompletion" && value && typeof value === "object") {
      return [field, t("manufacturing.completionDescription", {
        serialNumber: value.serialNumber,
        location: value.productionLocation,
        producedQuantity: value.producedQuantity,
      })];
    }
    return [field, value];
  };
  const historyActions = (order?.ManufacturingOrderActions || []).map((action) => ({
    ...action,
    changes: Object.fromEntries(Object.entries(action.changes || {})
      .map(([field, value]) => formatHistoryChange(field, value))),
  }));
  const handleClose = () => { if (!saving) onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogContent style={{ position: "relative", overflow: "hidden" }}>
        <DialogAppBar
          header={t(order ? "manufacturing.orderDetails" : "manufacturing.addOrder")}
          onClose={handleClose}
        />
        <ChangesHistoryLayout
          actions={historyActions}
          actionKey="manufacturing-order-action"
          labelPrefixes={["manufacturing"]}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          width={order ? width : "xs"}
          spacing={1}
          style={{ marginTop: 60, marginBottom: 20, paddingBottom: 30, position: "relative" }}
        >
          <Grid item xs={12}>
            <form id="manufacturing-order-form" onSubmit={submit}>
              <fieldset disabled={saving || !editing} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                <Grid container spacing={2}>
                  {order && <Grid item xs={12} sm={6}>
                    <TextField disabled={saving || !editing} fullWidth variant="outlined" label={t("manufacturing.orderNumber")}
                      value={order.uuid || ""} InputProps={{ readOnly: true }} />
                  </Grid>}
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={saving || !editing} select required fullWidth variant="outlined" label={t("manufacturing.orderWarehouse")}
                      value={form.warehouseId} onChange={field("warehouseId")}>
                      {warehouses.map((warehouse) => <MenuItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.label || warehouse.name}
                      </MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={saving || !editing} required fullWidth type="date" variant="outlined" label={t("manufacturing.orderDeadline")}
                      InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={field("dueDate")}
                      inputProps={{ min: "1000-01-01", max: "9999-12-31" }}
                      helperText={t("manufacturing.orderDeadlineHint")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField disabled={saving || !editing} select fullWidth variant="outlined" label={t("manufacturing.orderStatus")}
                      value={form.status} onChange={field("status")}>
                      {orderStatuses.map((status) => <MenuItem key={status} value={status}>
                        {t(`manufacturing.orderStatuses.${status}`)}
                      </MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">{t("manufacturing.orderQuotaTargets")}</Typography>
                    {order && <Typography variant="body2" color="textSecondary">{t("manufacturing.orderProgressHint")}</Typography>}
                  </Grid>
                  {items.map((item) => <Grid item xs={12} key={item.stockItemId}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={order ? 4 : 6}>
                        <Typography>{item.stockItem?.sku}</Typography>
                        <Typography variant="body2" color="textSecondary">{item.stockItem?.name}</Typography>
                      </Grid>
                      <Grid item xs={order ? 5 : 10} sm={order ? 3 : 4}>
                        <TextField disabled={saving || !editing} required fullWidth type="number" variant="outlined" label={t("manufacturing.orderedQuantity")}
                          inputProps={{ min: 1, max: 2147483647, step: 1 }} value={item.orderedQuantity}
                          onChange={(event) => changeQuantity(item.stockItemId, "orderedQuantity", event.target.value)} />
                      </Grid>
                      {order && <Grid item xs={5} sm={3}>
                        <TextField disabled={saving || !editing} required fullWidth type="number" variant="outlined" label={t("manufacturing.producedQuantity")}
                          inputProps={{ min: 0, max: 2147483647, step: 1 }} value={item.producedQuantity}
                          onChange={(event) => changeQuantity(item.stockItemId, "producedQuantity", event.target.value)} />
                      </Grid>}
                      <Grid item xs={2}>
                        <Button size="small" disabled={saving || !editing || Number(item.producedQuantity) > 0}
                          onClick={() => setItems(items.filter((row) => row.stockItemId !== item.stockItemId))}>
                          {t("manufacturing.removeOrderItem")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>)}
                  <Grid item xs={12}>
                    {editing && !saving && <StockItemSearch allowNewItems={false} clearOnSelect disableGridWrapper
                      label={t("manufacturing.addOrderItem")} onChange={(_, stockItem) => {
                        if (!stockItem?.id || items.some((item) => item.stockItemId === stockItem.id)) return;
                        setItems([...items, { stockItemId: stockItem.id, stockItem, orderedQuantity: 1, producedQuantity: 0 }]);
                        setValidationError("");
                      }} />}
                  </Grid>
                  {editing && (validationError || error) && <Grid item xs={12}>
                    <Typography color="error" role="alert">{validationError || error}</Typography>
                  </Grid>}
                </Grid>
              </fieldset>
            </form>
          </Grid>
          {order && <Grid item xs={12}>
            <DetailTabs key={`${order.id}-${width === "xs" ? "mobile" : "desktop"}`} activeTabs={[
              {
                label: <><ChatIcon />{t("notes")}</>,
                content: <NoteSection parent={{ ManufacturingOrderId: order.id }} notes={order.Notes}
                  reload={onRefresh} noPaper />,
              },
              width === "xs" ? {
                label: t("ticketList.changesHistory"),
                content: <ChangesHistory actions={historyActions} actionKey="manufacturing-order-action"
                  labelPrefixes={["manufacturing"]} />,
              } : null,
            ]} />
          </Grid>}
          <Grid item xs={12} style={{ height: 100 }} />
        </ChangesHistoryLayout>
        <div style={{
          position: "absolute", bottom: 0, left: 0, width: "100%", padding: 10,
          backgroundColor: "white", boxShadow: "#d6d6d6 0px 0px 15px 0px", zIndex: 90,
        }}>
          <Grid container spacing={1}>
            {order && !editing ? <Grid item>
              <Button variant="outlined" disabled={saving} onClick={() => setEditing(true)}>
                {t("edit")}
              </Button>
            </Grid> : <>
              <Grid item>
                <Button variant="contained" color="primary" type="submit"
                  form="manufacturing-order-form" disabled={saving}>
                  {t(saving ? "common.saving" : "common.save", saving ? "Saving…" : "Save")}
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" disabled={saving} onClick={() => {
                  if (!order) handleClose();
                  else { resetForm(); setEditing(false); }
                }}>{t("common.cancel", "Cancel")}</Button>
              </Grid>
            </>}
            {order && !editing && <Grid item>
              <Button variant="outlined" disabled={saving} onClick={handleClose}>{t("close")}</Button>
            </Grid>}
          </Grid>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default withWidth()(OrderDialog);
